import { useEffect, useState } from "react";
import { createBoVenue, deleteBoVenue, listBoVenues, renameBoVenue, type BoVenue } from "../../api";
import { fill, shortDate, venueLabel, type BoProps } from "./Backoffice";

type VenuesProps = BoProps & { onIssueCode: (venueId: string) => void };

export function Venues({ t, locale, onError, onIssueCode }: VenuesProps) {
  const [venues, setVenues] = useState<BoVenue[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The outcome of the last create or delete, shown above the table.
  const [notice, setNotice] = useState<string | null>(null);

  const load = () =>
    listBoVenues()
      .then((r) => {
        setVenues(r.venues);
        setLoadError(null);
      })
      .catch((e) => setLoadError(onError(e)));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bo">
      <AddVenue
        t={t}
        onError={onError}
        onAdded={(v) => {
          setNotice(fill(t("bo.venues.added"), { venue: venueLabel(v) }));
          void load();
        }}
      />

      <h2>{t("bo.nav.venues")}</h2>
      <p className="muted">{t("bo.venues.hint")}</p>
      {notice && <p className="ok">{notice}</p>}
      {loadError && <p className="error">{loadError}</p>}
      {venues.length === 0 && !loadError ? (
        <p className="muted">{t("common.empty")}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t("bo.venues.name")}</th>
                <th>{t("bo.venues.neighborhood")}</th>
                <th>{t("bo.venues.codes")}</th>
                <th>{t("bo.venues.staff")}</th>
                <th>{t("bo.venues.created")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {venues.map((v) => (
                <VenueRow
                  key={v.id}
                  v={v}
                  t={t}
                  locale={locale}
                  onError={onError}
                  onIssueCode={onIssueCode}
                  onSaved={load}
                  onDeleted={(name) => {
                    setNotice(fill(t("bo.venues.deleted"), { name }));
                    void load();
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AddVenue({ t, onError, onAdded }: Pick<BoProps, "t" | "onError"> & { onAdded: (v: BoVenue) => void }) {
  const [name, setName] = useState("");
  const [hood, setHood] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const v = await createBoVenue(name.trim(), hood.trim());
      setName("");
      setHood("");
      onAdded(v);
    } catch (err) {
      setError(onError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={submit}>
      <h2>{t("bo.venues.add_title")}</h2>
      <div className="inline-form">
        <input value={name} maxLength={80} placeholder={t("bo.venues.name")} aria-label={t("bo.venues.name")} onChange={(e) => setName(e.target.value)} />
        <input
          value={hood}
          maxLength={80}
          placeholder={t("bo.venues.neighborhood")}
          aria-label={t("bo.venues.neighborhood")}
          onChange={(e) => setHood(e.target.value)}
        />
        <button type="submit" disabled={busy || name.trim() === ""}>
          {t("bo.venues.add")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </form>
  );
}

type Mode = "view" | "edit" | "delete";

function VenueRow({
  v,
  t,
  locale,
  onError,
  onIssueCode,
  onSaved,
  onDeleted,
}: Omit<VenuesProps, "onIssueCode"> & {
  v: BoVenue;
  onIssueCode: (venueId: string) => void;
  onSaved: () => void;
  onDeleted: (name: string) => void;
}) {
  const [mode, setMode] = useState<Mode>("view");
  const [name, setName] = useState(v.name);
  const [hood, setHood] = useState(v.neighborhood);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function open(m: Mode) {
    setName(v.name);
    setHood(v.neighborhood);
    setError(null);
    setMode(m);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await renameBoVenue(v.id, name.trim(), hood.trim());
      setMode("view");
      onSaved();
    } catch (err) {
      setError(onError(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      const res = await deleteBoVenue(v.id);
      onDeleted(res.name);
    } catch (err) {
      setError(onError(err));
      setBusy(false);
    }
  }

  if (mode === "edit") {
    return (
      <tr className="edit-row">
        <td colSpan={6}>
          <form className="inline-form" onSubmit={save}>
            <input value={name} maxLength={80} autoFocus onChange={(e) => setName(e.target.value)} aria-label={t("bo.venues.name")} />
            <input
              value={hood}
              maxLength={80}
              placeholder={t("bo.venues.neighborhood")}
              onChange={(e) => setHood(e.target.value)}
              aria-label={t("bo.venues.neighborhood")}
            />
            <button type="submit" disabled={busy || name.trim() === ""}>
              {t("bo.venues.save")}
            </button>
            <button type="button" className="link" onClick={() => setMode("view")} disabled={busy}>
              {t("common.cancel")}
            </button>
          </form>
          <p className="muted small">{t("bo.venues.rename_note")}</p>
          {error && <p className="error">{error}</p>}
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr className={v.protected ? "dim" : ""}>
        <td className="strong">{v.name}</td>
        <td>{v.neighborhood || <span className="muted">—</span>}</td>
        <td>{v.live_codes}</td>
        <td>{v.staff}</td>
        <td className="muted">{shortDate(v.created_at, locale)}</td>
        <td className="row-actions">
          {v.protected ? (
            <span className="muted small">{t("bo.venues.protected")}</span>
          ) : (
            mode === "view" && (
              <>
                <button className="link" onClick={() => onIssueCode(v.id)}>
                  {t("bo.venues.issue")}
                </button>
                <button className="link" onClick={() => open("edit")}>
                  {t("bo.venues.edit")}
                </button>
                {/* Offered only when nothing depends on the venue; the server
                    checks again, since the list can be minutes old. */}
                {v.deletable && (
                  <button className="danger-link" onClick={() => open("delete")}>
                    {t("bo.venues.delete")}
                  </button>
                )}
              </>
            )
          )}
        </td>
      </tr>
      {mode === "delete" && (
        <tr className="confirm-row">
          <td colSpan={6}>
            <div className="confirm">
              <span>
                {fill(t(v.live_codes > 0 ? "bo.venues.delete_warn_codes" : "bo.venues.delete_warn"), {
                  name: venueLabel(v),
                  codes: v.live_codes,
                })}
              </span>
              <button className="danger" onClick={remove} disabled={busy}>
                {t("bo.venues.delete_confirm")}
              </button>
              <button className="link" onClick={() => setMode("view")} disabled={busy}>
                {t("common.cancel")}
              </button>
            </div>
            {error && <p className="error">{error}</p>}
          </td>
        </tr>
      )}
    </>
  );
}
